import { useRecoilState, useRecoilValue } from "recoil"

import { browsingHistoriesState, jobseekerState } from "../recoils"
import { companyState } from "../recoils/companyState"
import { recruitsState } from "../recoils/recruitsState"
import {
  addInterested,
  getBrowsingHistories,
  getJobseeker,
} from "../usecases/firebase/functions"

import type { Jobseeker, JobseekerId } from "../interfaces/IJobseeker"
import type { RecruitId } from "../interfaces/RecruitIF"

const dummyJobseekers: Jobseeker[] = []
for (let i = 0; i < 20; i++) {
  dummyJobseekers.push({
    id: "sample" + i,
    recruitId: "sample",
    desiredShip: "船の種類",
    desiredSalary: "希望月収",
    desiredPrefecture: "希望勤務地",
    licenses: ["取得免状1", "取得免状2"],
    isInterested: true,
  })
}

type PostInterestedProps = {
  jobseekerId: JobseekerId
  recruitId?: RecruitId
}

export const useJobseeker = () => {
  const company = useRecoilValue(companyState)
  const recruits = useRecoilValue(recruitsState)
  const [browsingHistories, setBrowsingHistories] = useRecoilState<Jobseeker[]>(
    browsingHistoriesState,
  )
  const [jobseekers, setJobseekers] =
    useRecoilState<Jobseeker[]>(jobseekerState)

  const disabledVisitHistory = !company?.plan || company.plan < 1

  const fetchJobseekers = async () => {
    if (disabledVisitHistory) {
      setJobseekers(dummyJobseekers)
      return
    }

    await fetchBrowsingHistories()
    const res = await getJobseeker()
    const list = res.data.map((j) => {
      const jobseeker = browsingHistories.find((bh) => j.id === bh.id)
      if (!jobseeker) return j

      if (j.id === jobseeker.id && jobseeker.isInterested) {
        return { ...j, isInterested: true }
      }
      return j
    })
    setJobseekers([])
    setJobseekers(list)
  }

  const fetchBrowsingHistories = async () => {
    if (disabledVisitHistory) {
      setJobseekers(dummyJobseekers)
      return
    }
    const res = await getBrowsingHistories()
    setBrowsingHistories([])
    setBrowsingHistories(res.data)
  }

  const postInterested = async (props: PostInterestedProps) => {
    const jobseekerId = props.jobseekerId
    const targetRecruitId = props.recruitId ?? recruits[0].recruitId

    await addInterested({
      jobseekerId: jobseekerId,
      recruitId: targetRecruitId,
    })

    const updatedJobseekers = jobseekers.map((j) => {
      if (j.id === jobseekerId) {
        return { ...j, isInterested: true }
      }
      return j
    })
    setJobseekers(updatedJobseekers)

    const updatedBrowsingHistories = browsingHistories.map((j) => {
      if (j.id === jobseekerId) {
        return { ...j, isInterested: true }
      }
      return j
    })
    setBrowsingHistories(updatedBrowsingHistories)
  }

  return {
    fetchJobseekers,
    fetchBrowsingHistories,
    postInterested,
  }
}
